import {
  CreateTaxonomyTranslationRequest,
  FeedbackSubmissionRequest,
  FieldsListParams,
  FieldTranslation,
  ListValueTranslation,
  Field,
  TaxonomyTranslation,
  TaxonomyTranslationPaginationParams,
  TaxonomyTranslationPaginationResponse,
  TaxonomyTranslationReviewProgress,
  UpdateTaxonomyTranslationRequest,
  UpdateTranslationReviewRequest,
  UpdateTranslationListValueReviewRequest,
  ExcludeFieldTranslationRequest,
  ExcludeListValueTranslationRequest,
} from '@taxonomy-translation/types'
import { GET, POST, PATCH, DELETE } from '../endpoints'
import { fetchData, postData, patchData, deleteData } from '../utils'

export async function getTaxonomyTranslations(
  params: TaxonomyTranslationPaginationParams,
  signal?: AbortSignal,
) {
  return fetchData<TaxonomyTranslationPaginationResponse>({
    endpoint: GET.taxonomyTranslations(),
    params,
    signal,
  })
}

export async function createTaxonomyTranslation(request: CreateTaxonomyTranslationRequest) {
  const formData = new FormData()

  if ('reference_templates' in request) {
    formData.append('reference_templates', request.reference_templates.join(','))
  } else {
    formData.append('reference_trafficking', request.reference_trafficking)

    if (request.reference_list_values) {
      formData.append('reference_list_values', request.reference_list_values)
    }
  }

  if ('candidate_templates' in request) {
    formData.append('candidate_templates', request.candidate_templates.join(','))
  } else {
    formData.append('candidate_trafficking', request.candidate_trafficking)

    if (request.candidate_list_values) {
      formData.append('candidate_list_values', request.candidate_list_values)
    }
  }

  return postData<TaxonomyTranslation, FormData>({
    endpoint: POST.taxonomyTranslations(),
    queryOptions: formData,
  })
}

export async function getTaxonomyTranslation(translationUuid: string, signal?: AbortSignal) {
  return fetchData<TaxonomyTranslation>({
    endpoint: GET.taxonomyTranslation(translationUuid),
    signal,
  })
}

export async function updateTaxonomyTranslation(
  translationUuid: string,
  request: UpdateTaxonomyTranslationRequest,
) {
  return patchData<TaxonomyTranslation, UpdateTaxonomyTranslationRequest>({
    endpoint: PATCH.taxonomyTranslation(translationUuid),
    queryOptions: request,
  })
}

export async function deleteTaxonomyTranslation(translationUuid: string) {
  return deleteData<TaxonomyTranslation>({
    endpoint: DELETE.taxonomyTranslation(translationUuid),
  })
}

export async function getTaxonomyTranslationProgress(
  translationUuid: string,
  signal?: AbortSignal,
) {
  return fetchData<TaxonomyTranslationReviewProgress>({
    endpoint: GET.taxonomyTranslationProgress(translationUuid),
    signal,
  })
}

export async function getTaxonomyTranslationFieldTranslations(
  translationUuid: string,
  signal?: AbortSignal,
) {
  return fetchData<FieldTranslation[]>({
    endpoint: GET.taxonomyTranslationFieldTranslations(translationUuid),
    signal,
  })
}

export async function updateFieldTranslationReview(
  translationUuid: string,
  { fieldId, ...request }: UpdateTranslationReviewRequest,
) {
  return patchData<FieldTranslation, Omit<UpdateTranslationReviewRequest, 'uuid' | 'fieldId'>>({
    endpoint: PATCH.taxonomyTranslationFieldTranslation(translationUuid, fieldId),
    queryOptions: request,
  })
}

export async function excludeFieldTranslation(
  translationUuid: string,
  { fieldId }: ExcludeFieldTranslationRequest,
) {
  return deleteData<FieldTranslation>({
    endpoint: DELETE.taxonomyTranslationFieldTranslation(translationUuid, fieldId),
  })
}

export async function updateListValueTranslationReview(
  translationUuid: string,
  { fieldId, index, ...request }: UpdateTranslationListValueReviewRequest,
) {
  return patchData<
    ListValueTranslation,
    Omit<UpdateTranslationListValueReviewRequest, 'uuid' | 'fieldId' | 'index'>
  >({
    endpoint: PATCH.taxonomyTranslationListValueTranslation(translationUuid, fieldId, index),
    queryOptions: request,
  })
}

export async function excludeListValueTranslation(
  translationUuid: string,
  { fieldId, index }: ExcludeListValueTranslationRequest,
) {
  return deleteData<FieldTranslation>({
    endpoint: DELETE.taxonomyTranslationListValueTranslation(translationUuid, fieldId, index),
  })
}

export async function submitTaxonomyTranslationsFeedback(request: FeedbackSubmissionRequest) {
  return postData<never, FeedbackSubmissionRequest>({
    endpoint: POST.taxonomyTranslationsFeedback(),
    queryOptions: request,
  })
}

export async function getTaxonomyTranslationReferenceFields(
  translationUuid: string,
  params: FieldsListParams = {},
  signal?: AbortSignal,
) {
  return fetchData<Field[]>({
    endpoint: GET.taxonomyTranslationReferenceFields(translationUuid),
    params,
    signal,
  })
}

export async function exportTaxonomyTranslation(translationUuid: string) {
  return fetchData<Blob>({
    endpoint: GET.exportTaxonomyTranslation(translationUuid),
    responseType: 'blob',
  })
}
